/**
 * EVOLUTION front-end (implements UiApi): title screen with a live 3D background, opening
 * cinematic, HUD, narrator, touch controls, loading/stage cards, pause/settings, devolve
 * overlay, the evolution transition cinematic, the Evolution Tree codex and endings.
 * Owned by the ux agent. Keep the UiApi/HudApi/NarratorApi contracts intact.
 */
import type { Camera, Scene } from 'three';
import type { Game } from '@core/Game';
import type { EndingInfo, TransitionInfo, UiApi } from '@core/services';
import { ENDING_STAGES, TREE, type ActionLabelKey, type EvoNode, type StageId } from '@core/tree';
import { el, escapeHtml, fmtTime, letters, prefersReducedMotion, wait } from './dom';
import { Hud } from './hud/Hud';
import { Narrator } from './hud/Narrator';
import { TouchControls } from './hud/Touch';
import { BRANCH_ICONS, ICONS } from './icons';
import { DEATH_QUIPS, DEATH_QUIPS_MANY, ENDING_LINES, IDLE_QUIPS, LOADING_TIPS, TRANSITION_LINES, fill, HUD_HINT_IDLE } from './lines';
import { MenuNav, navButton } from './nav';
import { FullscreenHelper, fullscreenApiAvailable, isFullscreen, requestFullscreen } from './fullscreen';
import { Codex } from './screens/Codex';
import { Intro } from './screens/Intro';
import { IntroVideo } from './screens/IntroVideo';
import { buildSettings } from './screens/Settings';
import { buildControls } from './screens/Controls';
import { buildTreeSvg } from './screens/treeSvg';
import { PedestalScene } from './three/PedestalScene';
import { TitleScene } from './three/TitleScene';
import { TransitionScene } from './three/TransitionScene';
import { loadUiSettings, saveUiSettings, type UiSettings } from './uiSettings';
import './styles.css';

interface AuxView {
  scene: Scene;
  camera: Camera;
  update?(dt: number): void;
}

const KEY_ACTIONS: Record<string, ActionLabelKey> = {
  KeyJ: 'attack',
  ShiftLeft: 'dash',
  ShiftRight: 'dash',
  KeyK: 'dash',
  Space: 'jump',
  KeyQ: 'ability',
  KeyF: 'ability',
  KeyL: 'ability',
  KeyE: 'interact',
};

export class UI implements UiApi {
  readonly root: HTMLElement;
  readonly hud: Hud;
  readonly narrator: Narrator;
  readonly nav = new MenuNav();
  private touch: TouchControls;
  private uiSettings: UiSettings = loadUiSettings();
  private title = el('div', 'screen title-screen');
  private loading = el('div', 'screen loading-screen');
  private pauseEl = el('div', 'screen modal pause');
  private modalEl = el('div', 'screen modal dim');
  private devolveEl = el('div', 'screen devolve');
  private transitionEl = el('div', 'screen transition');
  private endingEl = el('div', 'screen ending');
  private card = el('div', 'stage-card');
  private rotate = el('div', 'rotate-prompt', `${ICONS.rotate}<div class="t">Rotate your device</div><div class="s">EVOLUTION plays in landscape.</div>`);
  private fps = el('div', 'fps');
  private codex: Codex;
  private fs: FullscreenHelper;
  /** Gameplay was paused by the iOS swipe prompt (auto-resume when it hides). */
  private pausedByFs = false;
  private intro: Intro;
  private introVideo: IntroVideo;
  private titleScene?: TitleScene;
  private auxStack: AuxView[] = [];
  private tipIndex = Math.floor(Math.random() * LOADING_TIPS.length);
  private lastGrowthAt = 0;
  private lastGrowth = -1;
  private idleQuipAt = 0;
  private starting = false;

  constructor(private readonly game: Game, root: HTMLElement) {
    this.root = root;
    this.hud = new Hud();
    this.narrator = new Narrator(game.audio);
    this.touch = new TouchControls(game.input);
    this.codex = new Codex({
      game,
      nav: this.nav,
      pushAux: (v) => this.pushAux(v),
      popAux: () => this.popAux(),
      onClose: () => {
        this.game.audio.sfx('ui_back');
        this.closeCodex();
      },
      startAt: (id) => {
        this.codexUnder = [];
        this.codex.hide();
        void this.startRun(id);
      },
      explorer: () => this.uiSettings.explorer,
    });
    this.intro = new Intro(game.audio);
    this.introVideo = new IntroVideo(game.audio);

    this.hud.onActions = (l) => this.touch.setActions(l);
    this.hud.onCooldown = (a, f) => this.touch.setCooldown(a, f);
    this.hud.onPauseTap = () => game.togglePause();
    this.hud.onExplorerTap = () => game.pause();
    this.hud.onFullscreenTap = () => this.toggleFullscreen();
    this.hud.onEvolveTap = (down) => game.input.setAction('interact', down, 'touch:evolve');
    this.hud.onGrowthNotch = (i) => {
      if (i < 2) game.audio.sfx('growth', { volume: 0.5 });
    };
    this.touch.onPress = (a, d) => this.hud.pressAction(a, d);
    this.hud.onEvolveVisible = (v) => this.touch.setEvolveVisible(v);
    this.fs = new FullscreenHelper(game.quality.isTouch);
    document.body.append(this.fs.root);
    // Gameplay can't be controlled while the swipe layer is up — pause it.
    this.pausedByFs = false;
    this.fs.onChange = (showing) => {
      if (showing && game.screen === 'playing') {
        game.pause();
        this.pausedByFs = true;
      } else if (!showing && this.pausedByFs) {
        // Toolbars collapsed → pick up where the player left off.
        this.pausedByFs = false;
        if (game.screen === 'paused') game.resume();
      }
    };
    if (game.quality.isTouch) {
      if (fullscreenApiAvailable()) this.hud.addFullscreenButton();
      this.rotate.classList.add('armed');
    }

    // Touch zones sit BELOW the HUD so the pause button / evolve prompt stay tappable (#5).
    root.append(
      this.touch.root,
      this.hud.root,
      this.narrator.root,
      this.card,
      this.title,
      this.loading,
      this.pauseEl,
      this.devolveEl,
      this.transitionEl,
      this.endingEl,
      this.codex.root,
      this.modalEl,
      this.intro.root,
      this.introVideo.root,
      this.rotate,
      this.fps,
    );

    // Audio unlock on the first gesture; hover/click sounds for anything navigable.
    const unlock = () => void game.audio.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    let lastHover: Element | null = null;
    root.addEventListener('pointerover', (e) => {
      const t = (e.target as HTMLElement).closest?.('[data-nav]');
      if (t && t !== lastHover) game.audio.sfx('ui_hover', { volume: 0.35 });
      lastHover = t ?? null;
    });
    root.addEventListener('click', (e) => {
      const t = (e.target as HTMLElement).closest?.('button.btn, .tab');
      if (t) game.audio.sfx('ui_click', { volume: 0.7 });
    });
    this.nav.onMove = () => game.audio.sfx('ui_hover', { volume: 0.35 });
    this.nav.onGamepad = () => this.hud.setDevice('gamepad');

    // Keep HUD glyphs in sync with the active device; action-bar press feedback.
    setInterval(() => {
      const d = game.input.device;
      this.hud.setDevice(d === 'keyboard' && game.quality.isTouch ? 'touch' : d);
    }, 300);
    window.addEventListener('keydown', (e) => {
      const a = KEY_ACTIONS[e.code];
      if (a && !e.repeat) this.hud.pressAction(a, true);
    });
    window.addEventListener('keyup', (e) => {
      const a = KEY_ACTIONS[e.code];
      if (a) this.hud.pressAction(a, false);
    });

    game.events.on('screen', ({ screen }) => {
      this.nav.suspended = screen === 'playing';
      this.touch.root.classList.toggle('off', screen !== 'playing');
      // Play started while the iOS swipe prompt is up → pause until the toolbars are gone.
      if (screen === 'playing' && this.fs?.showing)
        setTimeout(() => {
          if (this.fs.showing && game.screen === 'playing') {
            game.pause();
            this.pausedByFs = true;
          }
        }, 0);
      if (screen !== 'playing') this.touch.releaseAll();
    });
    game.events.on('stage:start', ({ stage }) => {
      this.hud.setNode(TREE[stage], game.run.data.history.length);
      this.lastGrowthAt = performance.now();
      this.idleQuipAt = 0;
    });
    game.events.on('stage:growth', ({ growth }) => {
      if (growth > this.lastGrowth + 0.005) this.lastGrowthAt = performance.now();
      this.lastGrowth = growth;
    });
    // Idle quips when the player makes no progress for a while.
    setInterval(() => {
      if (game.screen !== 'playing' || game.debug.autopilot) return;
      const now = performance.now();
      if (now - this.lastGrowthAt > HUD_HINT_IDLE * 1000 && now - this.idleQuipAt > 60000) {
        this.idleQuipAt = now;
        void this.narrator.sayOneOf(IDLE_QUIPS, { mood: 'bored', priority: 0 });
      }
    }, 1000);
    window.addEventListener('resize', () => this.applyUiSettings());
    this.applyUiSettings();
  }

  // ---------------------------------------------------------------------------
  // Settings / helpers
  // ---------------------------------------------------------------------------

  private applyUiSettings(): void {
    this.hud.setExplorer(this.uiSettings.explorer);
    const s = this.game.profile.settings;
    const u = this.uiSettings;
    const html = document.documentElement;
    const reduced = s.reducedMotion || prefersReducedMotion();
    html.classList.toggle('reduced-motion', reduced);
    html.classList.toggle('colorblind', u.colorBlind);
    html.classList.toggle('tier-low', this.game.quality.tier === 'low');
    html.classList.toggle('is-touch', this.game.quality.isTouch);
    html.style.setProperty('--hud-scale', String(u.hudScale));
    html.style.setProperty('--sub-scale', String(u.subtitleScale));
    this.touch.setLayout({ scale: u.touchScale, leftHanded: u.leftHanded });
    this.narrator.subtitles = s.subtitles;
    this.narrator.instant = reduced;
  }

  private saveUi(patch: Partial<UiSettings>): void {
    Object.assign(this.uiSettings, patch);
    saveUiSettings(this.uiSettings);
    this.applyUiSettings();
  }

  private pushAux(v: AuxView): void {
    this.auxStack = this.auxStack.filter((x) => x !== v);
    this.auxStack.push(v);
    this.game.setAuxView(v);
  }

  private popAux(v?: AuxView): void {
    if (v) this.auxStack = this.auxStack.filter((x) => x !== v);
    else this.auxStack.pop();
    this.game.setAuxView(this.auxStack[this.auxStack.length - 1] ?? null);
  }

  private clearAux(): void {
    this.auxStack = [];
    this.game.setAuxView(null);
  }

  private show(e: HTMLElement, v: boolean): void {
    e.classList.toggle('visible', v);
  }

  private toggleFullscreen(): void {
    if (isFullscreen()) void document.exitFullscreen?.();
    else void requestFullscreen();
  }

  // ---------------------------------------------------------------------------
  // Title / menus
  // ---------------------------------------------------------------------------

  showTitle(opts: { canContinue: boolean }): void {
    if (!this.uiSettings.seenIntro && !this.uiSettings.skipIntro) this.introVideo.preload();
    const g = this.game;
    this.hideEnding();
    this.titleScene ??= new TitleScene(g.quality);
    this.titleScene.plunge = 0;
    this.clearAux();
    this.pushAux(this.titleScene);
    g.renderSystem.configure({ bloom: { intensity: 1.25, threshold: 0.3, smoothing: 0.3, radius: 0.8 }, vignette: { offset: 0.25, darkness: 0.7 }, chromatic: 0.0006, exposure: 1 });

    const found = g.profile.data.discovered.length;
    const endings = g.profile.data.endings.filter((e) => ENDING_STAGES.includes(e)).length;
    this.title.innerHTML = `
      <div class="logo-wrap">
        <div class="logo-kicker">An evolution ray misfired</div>
        <h1 class="logo" aria-label="EVOLUTION">${letters('EVOLUTION')}</h1>
        <div class="logo-under"></div>
      </div>
      <p class="title-tag">You are a <b>single cell</b>. Eat, grow, adapt — and climb the tree of life. 16 forms. 9 endings.</p>
      <div class="title-menu"></div>
      <div class="gp-hint"><span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span><span><kbd>Enter</kbd> Select</span><span><kbd class="pad">A</kbd> Gamepad</span></div>
      <div class="title-foot"><div class="stats"><span>Forms <b>${found}/16</b></span><span>Endings <b>${endings}/${ENDING_STAGES.length}</b></span></div><div class="version">v${escapeHtml(__EVO_VERSION__)}</div></div>
      <div class="title-corner"></div>`;
    const menu = this.title.querySelector('.title-menu') as HTMLElement;
    const btns: HTMLButtonElement[] = [];
    if (opts.canContinue) {
      const saved = this.savedStage();
      btns.push(navButton(`<span>Continue<span class="sub">${saved ? escapeHtml(TREE[saved].name) : ''}</span></span>`, 'primary', () => void this.continueRun()));
    }
    btns.push(
      navButton('New Evolution', opts.canContinue ? '' : 'primary', () => this.onNewEvolution(opts.canContinue)),
      this.uiSettings.explorer
        ? navButton(`${ICONS.tree}<span>Explore Stages<span class="sub">Evolution Tree · jump to any form</span></span>`, 'explore', () => this.openCodex())
        : navButton(`${ICONS.tree}Evolution Tree`, '', () => this.openCodex()),
      navButton(`${ICONS.gear}Settings`, '', () => this.openSettings()),
    );
    btns.forEach((b, i) => {
      b.classList.add('stagger');
      b.style.setProperty('--i', String(i));
      menu.append(b);
    });
    btns[0].dataset.navDefault = '';
    const corner = this.title.querySelector('.title-corner') as HTMLElement;
    if (fullscreenApiAvailable()) {
      const fs = el('button', 'hud-btn', ICONS.fullscreen);
      fs.setAttribute('aria-label', 'Fullscreen');
      fs.addEventListener('click', () => this.toggleFullscreen());
      corner.append(fs);
    }
    this.show(this.title, true);
    this.nav.clear();
    this.nav.push({ root: this.title });
    this.starting = false;
  }

  private savedStage(): StageId | undefined {
    try {
      const raw = localStorage.getItem('evo.run.v1');
      const d = raw ? (JSON.parse(raw) as { stage?: StageId }) : undefined;
      return d?.stage && TREE[d.stage] ? d.stage : undefined;
    } catch {
      return undefined;
    }
  }

  private async continueRun(): Promise<void> {
    if (this.starting) return;
    this.starting = true;
    await this.plunge();
    await this.game.continueRun();
  }

  private onNewEvolution(hasSave: boolean): void {
    if (!hasSave) return void this.newEvolution();
    const saved = this.savedStage();
    const panel = el('div', 'panel glass confirm');
    panel.append(
      el('h2', '', 'Start over?'),
      el('div', 'sub', `Your current run${saved ? ` (${escapeHtml(TREE[saved].name)})` : ''} will be devolved back to a single cell.`),
    );
    const row = el('div', 'btn-row');
    const close = () => {
      this.show(this.modalEl, false);
      this.nav.remove(this.modalEl);
    };
    const yes = navButton('Devolve & Start', 'danger', () => {
      close();
      void this.newEvolution();
    });
    row.append(yes, navButton('Cancel', '', close));
    panel.append(row);
    this.modalEl.innerHTML = '';
    this.modalEl.append(panel);
    this.show(this.modalEl, true);
    this.nav.push({ root: this.modalEl, onBack: close });
  }

  private async newEvolution(): Promise<void> {
    if (this.starting) return;
    this.starting = true;
    await this.plunge();
    // Automated browsers (QA) skip the cinematic unless ?intro=1 asks for it.
    const force = new URLSearchParams(location.search).has('intro');
    // The cinematic plays once (first New Evolution); after that title → gameplay is one tap.
    const playIntro = force || (!this.uiSettings.skipIntro && !this.uiSettings.seenIntro && !navigator.webdriver);
    if (playIntro) {
      this.show(this.title, false);
      this.nav.clear();
      this.game.audio.music(null, { fade: 0.4 });
      // Video cinematic first; the in-engine SVG intro is the fallback if the video can't play.
      const useVideo = !new URLSearchParams(location.search).has('svgintro');
      const result = useVideo ? await this.runSkippable(this.introVideo.root, () => this.introVideo.skip(), () => this.introVideo.play({ subtitles: this.game.profile.settings.subtitles })) : 'failed';
      if (result === 'failed') {
        await this.runSkippable(this.intro.root, () => this.intro.skip(), () =>
          this.intro.play({ subtitles: this.game.profile.settings.subtitles, reducedMotion: this.game.profile.settings.reducedMotion }),
        );
      }
      this.saveUi({ seenIntro: true });
    }
    if (playIntro) {
      // The cinematic ends on a whiteout; fade from white into the loading screen.
      this.loading.classList.add('flash-in');
      setTimeout(() => this.loading.classList.remove('flash-in'), 1200);
    }
    await this.startRun('cell');
  }

  /** Run a cinematic with one-tap / Esc / Enter / Space / gamepad skip. */
  private async runSkippable<T>(root: HTMLElement, skip: () => void, run: () => Promise<T>): Promise<T> {
    this.nav.push({ root, onBack: skip, onStart: skip });
    const onKey = (e: KeyboardEvent) => {
      if (['Escape', 'Enter', 'Space'].includes(e.code)) skip();
    };
    root.addEventListener('pointerdown', skip);
    window.addEventListener('keydown', onKey);
    try {
      return await run();
    } finally {
      window.removeEventListener('keydown', onKey);
      root.removeEventListener('pointerdown', skip);
      this.nav.remove(root);
    }
  }

  private async startRun(id: StageId): Promise<void> {
    this.starting = true;
    this.hidePause();
    await this.game.newRun(id);
  }

  /** Zoom the title camera into the portal before starting. */
  private async plunge(): Promise<void> {
    const ts = this.titleScene;
    if (!ts || !this.auxStack.includes(ts)) return;
    this.game.audio.sfx('portal', { volume: 0.7 });
    this.title.classList.add('leaving');
    const t0 = performance.now();
    const dur = this.game.profile.settings.reducedMotion ? 150 : 420;
    await new Promise<void>((resolve) => {
      const step = () => {
        const k = Math.min(1, (performance.now() - t0) / dur);
        ts.plunge = k;
        if (k < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });
    this.title.classList.remove('leaving');
  }

  hideTitle(): void {
    this.show(this.title, false);
    this.show(this.modalEl, false);
    this.codexUnder = [];
    this.codex.hide();
    this.hideEnding();
    this.nav.clear();
    this.clearAux();
  }

  /** Screens hidden while the codex is open (restored on close). */
  private codexUnder: HTMLElement[] = [];

  private openCodex(current?: StageId): void {
    this.codexUnder = [this.title, this.pauseEl, this.endingEl].filter((e) => e.classList.contains('visible'));
    for (const e of this.codexUnder) e.classList.remove('visible');
    this.codex.show(current);
  }

  private closeCodex(): void {
    this.codex.hide();
    for (const e of this.codexUnder) e.classList.add('visible');
    this.codexUnder = [];
  }

  /** Controls diagram (keyboard layouts, gamepad, touch). `back` reopens the caller if given. */
  private openControls(back?: () => void): void {
    const close = () => {
      this.show(this.modalEl, false);
      this.nav.remove(this.modalEl);
      this.game.audio.sfx('ui_back');
      back?.();
    };
    const panel = buildControls({ labels: this.game.node?.actions, onBack: close, isTouch: this.game.quality.isTouch });
    this.modalEl.innerHTML = '';
    this.modalEl.append(panel);
    this.show(this.modalEl, true);
    this.nav.push({ root: this.modalEl, onBack: close });
  }

  private openSettings(tab = 0): void {
    const close = () => {
      this.show(this.modalEl, false);
      this.nav.remove(this.modalEl);
      this.game.audio.sfx('ui_back');
    };
    const panel = buildSettings(
      {
        game: this.game,
        ui: this.uiSettings,
        saveUi: (p) => this.saveUi(p),
        onBack: close,
        onControls: () => this.openControls(() => this.openSettings(2)),
      },
      tab,
    );
    this.modalEl.innerHTML = '';
    this.modalEl.append(panel);
    this.show(this.modalEl, true);
    this.nav.push({ root: this.modalEl, onBack: close });
  }

  // ---------------------------------------------------------------------------
  // Loading / stage card
  // ---------------------------------------------------------------------------

  showLoading(node: EvoNode): void {
    this.tipIndex = (this.tipIndex + 1) % LOADING_TIPS.length;
    const hist = this.game.run.data.history;
    const lineage = hist[hist.length - 1] === node.id ? hist : [...hist, node.id];
    const dots = lineage
      .map((id, i) => `${i ? '<s></s>' : ''}<i class="${id === node.id ? 'cur' : 'on'}" title="${escapeHtml(TREE[id].name)}"></i>`)
      .join('');
    this.loading.innerHTML = `
      <div class="dna-spin">${Array.from({ length: 8 }, (_, i) => `<i style="--i:${i};top:${i * 8}px"></i>`).join('')}</div>
      <div class="load-era">${escapeHtml(node.era)}</div>
      <div class="load-name" style="color:${node.color}">${escapeHtml(node.name)}</div>
      <div class="load-bar"><div></div></div>
      <div class="load-tree">${dots}</div>
      <div class="load-tip"><b>Tip</b>${escapeHtml(LOADING_TIPS[this.tipIndex])}</div>`;
    this.show(this.loading, true);
  }

  setLoadingProgress(frac: number): void {
    const b = this.loading.querySelector('.load-bar > div') as HTMLElement | null;
    if (b) b.style.width = `${Math.round(Math.max(0, Math.min(1, frac)) * 100)}%`;
  }

  hideLoading(): void {
    this.show(this.loading, false);
  }

  async showStageCard(node: EvoNode): Promise<void> {
    const branches = node.paths.length
      ? `<div class="branches">${node.paths.map((p) => `<span style="color:${p.color}">${BRANCH_ICONS[p.key] ?? ICONS.dna}${escapeHtml(p.label)}</span>`).join('')}</div>`
      : `<div class="branches"><span style="color:var(--gold)">${ICONS.star}${node.kind === 'final' ? 'Final form' : 'Ending'}</span></div>`;
    this.card.innerHTML = `<div class="era">${escapeHtml(node.era)}</div><div class="name" style="color:${node.color}">${letters(node.name)}</div><div class="rule" style="color:${node.color}"></div><div class="tag">${escapeHtml(node.tagline)}</div>${branches}`;
    this.card.classList.add('visible');
    this.game.audio.sfx('ui_open', { volume: 0.6 });
    await wait(1000);
    this.card.classList.remove('visible');
    await wait(200);
  }

  // ---------------------------------------------------------------------------
  // Pause / devolve
  // ---------------------------------------------------------------------------

  showPause(): void {
    const g = this.game;
    const node = g.node;
    const panel = el('div', 'panel glass pause-panel');
    const head = el('div', 'panel-head', '<h2>Paused</h2>');
    panel.append(head);
    if (node) {
      panel.append(
        el(
          'div',
          'pause-creature',
          `<div class="dot" style="color:${node.color}"></div><div><div class="n" style="color:${node.color}">${escapeHtml(node.name)}</div><div class="t">${escapeHtml(node.tagline)}</div></div>`,
        ),
      );
      const lin = g.run.data.history.map((id) => `<span style="color:${TREE[id].color}"><i></i></span><span style="color:var(--ink-dim)">${escapeHtml(TREE[id].name)}</span>`).join('<span class="arr">›</span>');
      panel.append(el('div', 'mini-lineage', lin));
      panel.append(
        el(
          'div',
          'sub',
          `${ICONS.clock.replace('<svg ', '<svg style="width:14px;height:14px;display:inline;vertical-align:-2px" ')} ${fmtTime(g.run.data.playTime)} · Devolutions ${g.run.data.devolutions} · Traits ${g.run.data.traits.length}`,
        ),
      );
    }
    const col = el('div', 'btn-col');
    const resume = navButton(`${ICONS.play}Resume`, 'primary', () => g.resume());
    resume.dataset.navDefault = '';
    col.append(
      resume,
      navButton('Restart Stage', '', () => void g.restartStage()),
      navButton(`${ICONS.tree}${this.uiSettings.explorer ? 'Jump to…' : 'Evolution Tree'}`, '', () => this.openCodex(g.run.stage)),
      navButton(`${ICONS.gear}Settings`, '', () => this.openSettings()),
      navButton('Controls', '', () => this.openControls()),
      navButton('Quit to Title', 'danger', () => g.quitToTitle()),
    );
    panel.append(col);
    if (node && this.uiSettings.explorer) panel.append(this.skipSection(node));
    this.pauseEl.innerHTML = '';
    this.pauseEl.append(panel);
    this.show(this.pauseEl, true);
    this.nav.push({ root: this.pauseEl, onBack: () => g.resume(), onStart: () => g.resume() });
    this.game.audio.sfx('ui_open', { volume: 0.6 });
  }

  /** Stage explorer: one "Skip → next form" button per branch (real transition), or skip to ending. */
  private skipSection(node: EvoNode): HTMLElement {
    const g = this.game;
    const box = el('div', 'skip-box', `<h3>${ICONS.skip}Stage explorer</h3>`);
    const row = el('div', 'skip-row');
    const go = (fn: () => void) => () => {
      this.hidePause();
      g.resume();
      fn();
    };
    if (node.paths.length) {
      for (const p of node.paths) {
        const to = TREE[p.to];
        const b = navButton(
          `<i style="color:${p.color}">${BRANCH_ICONS[p.key] ?? ICONS.dna}</i><span>→ ${escapeHtml(to.name)}<span class="sub" style="color:${p.color}">${escapeHtml(p.label)}</span></span>`,
          'small skip-btn',
          go(() => void g.evolve(p.key)),
        );
        b.style.setProperty('--c', p.color);
        row.append(b);
      }
    } else {
      row.append(navButton(`${ICONS.star}<span>Skip to ending</span>`, 'small skip-btn', go(() => void g.finishRun())));
    }
    box.append(row);
    return box;
  }

  hidePause(): void {
    this.show(this.pauseEl, false);
    this.show(this.modalEl, false);
    this.codexUnder = [];
    this.codex.hide();
    this.nav.remove(this.modalEl);
    this.nav.remove(this.pauseEl);
  }

  async showDevolve(info: { node: EvoNode; cause?: string; devolutions: number }): Promise<void> {
    const auto = this.game.debug.autopilot;
    const minMs = auto ? 400 : 600;
    const maxMs = auto ? 400 : 2200;
    const d = this.devolveEl;
    d.innerHTML = `<div class="noise"></div><div class="scan"></div>
      <div class="dna-row">${Array.from({ length: 9 }, (_, i) => `<i style="--i:${i}"></i>`).join('')}</div>
      <h1 data-t="DEVOLVED">DEVOLVED</h1>
      <div class="cause">${info.cause ? escapeHtml(info.cause) : 'Natural selection happened'}</div>
      <div class="count">Devolutions this run: <b>${info.devolutions}</b></div>
      <div class="bar" style="--dur:${maxMs}ms"><i></i></div>`;
    const btn = navButton(`${ICONS.dna}Re-evolve`, 'small ghost', () => undefined);
    btn.style.marginTop = '8px';
    d.append(btn);
    this.show(d, true);
    this.hud.damageFlash(1);
    const lines = info.devolutions > 5 && Math.random() < 0.5 ? DEATH_QUIPS_MANY : DEATH_QUIPS;
    void this.narrator.sayOneOf(lines, { mood: 'snark', priority: 3 });
    const t0 = performance.now();
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        window.removeEventListener('keydown', onKey);
        d.removeEventListener('pointerdown', onTap);
        this.nav.remove(d);
        resolve();
      };
      const tryFinish = () => {
        if (performance.now() - t0 >= minMs) finish();
      };
      const onKey = () => tryFinish();
      const onTap = () => tryFinish();
      btn.addEventListener('click', tryFinish);
      window.addEventListener('keydown', onKey);
      d.addEventListener('pointerdown', onTap);
      this.nav.push({ root: d, onBack: tryFinish, onStart: tryFinish });
      const timer = setTimeout(finish, maxMs);
    });
    d.classList.add('leaving');
    await wait(auto ? 0 : 250);
    d.classList.remove('leaving');
    this.show(d, false);
  }

  // ---------------------------------------------------------------------------
  // Evolution transition
  // ---------------------------------------------------------------------------

  async playTransition(info: TransitionInfo, ready: Promise<void>): Promise<void> {
    const g = this.game;
    const t = this.transitionEl;
    const auto = g.debug.autopilot;
    let loaded = false;
    void ready.then(() => (loaded = true)).catch(() => (loaded = true));
    t.innerHTML = '';
    const snap = el('div', 'snap');
    if (info.snapshot) snap.style.backgroundImage = `url(${info.snapshot})`;
    const flash = el('div', 'flash');
    const rings = el('div', 'rings', '<i style="--i:0"></i><i style="--i:1"></i><i style="--i:2"></i>');
    const traits = info.traitsGained.length
      ? `<div class="traits">${info.traitsGained.map((x, i) => `<span style="--i:${i}">${ICONS.star}${escapeHtml(x.name)}</span>`).join('')}</div>`
      : '';
    const card = el(
      'div',
      'tcard',
      `<div class="from">${escapeHtml(info.from.name)}</div><div class="arrow">${escapeHtml(info.path.label)} ▸ evolves into</div>
       <div class="to" style="color:${info.to.color}">${letters(info.to.name)}</div><div class="era">${escapeHtml(info.to.era)}</div>${traits}`,
    );
    const skipHint = el('div', 'skip-hint', `<span>Skip</span><div class="prog"><i></i></div>`);
    t.append(snap, flash, rings, card, skipHint);
    this.show(t, true);
    this.narrator.setCinematic(true);

    if (auto) {
      await Promise.all([wait(600), ready.catch(() => undefined)]);
      this.show(t, false);
      this.narrator.setCinematic(false);
      return;
    }

    const scene = new TransitionScene({ assets: g.assets, quality: g.quality }, info.from.id, info.to.id);
    const loadP = scene.load().catch((err) => console.warn('[ui] transition previews failed', err));
    await Promise.race([loadP, wait(500)]);
    this.pushAux(scene);
    requestAnimationFrame(() => snap.classList.add('go'));
    g.audio.duck(0.35, 6);

    let skipped = false;
    const onSkip = () => {
      if (loaded) skipped = true;
    };
    t.addEventListener('pointerdown', onSkip);
    window.addEventListener('keydown', onSkip);
    this.nav.push({ root: t, onBack: onSkip, onStart: onSkip }, false);

    const lines = TRANSITION_LINES[info.to.id] ?? TRANSITION_LINES.default;
    const say = fill(lines[Math.floor(Math.random() * lines.length)], { path: info.path.label, to: info.to.name, from: info.from.name });
    // Reduced motion: same beats, compressed into ~3.4 s.
    // ≤3 s total (5.6 s of authored timeline at 2.3×); reduced motion a bit quicker still.
    const speed = g.profile.settings.reducedMotion || prefersReducedMotion() ? 2.6 : 2.3;
    const DUR = 5.6;
    const t0 = performance.now();
    let cardShown = false;
    let flashed = false;
    await new Promise<void>((resolve) => {
      const step = () => {
        const s = ((performance.now() - t0) / 1000) * speed;
        scene.energy = Math.min(1, s / 1.0) * (1 - Math.max(0, (s - 4.0) / 1.2) * 0.7);
        scene.progress = Math.min(1, Math.max(0, (s - 0.9) / 2.9));
        scene.reveal = Math.min(1, Math.max(0, (s - 3.7) / 1.2));
        if (!flashed && s > 3.55) {
          flashed = true;
          flash.classList.add('go');
          g.audio.sfx('evolve', { volume: 0.9 });
          void this.narrator.say(say, { mood: 'proud', priority: 4 });
        }
        if (!cardShown && s > 3.7) {
          cardShown = true;
          card.classList.add('show');
        }
        skipHint.classList.toggle('show', loaded && s > 1);
        (skipHint.querySelector('.prog i') as HTMLElement).style.setProperty('--p', `${Math.min(100, (s / DUR) * 100)}%`);
        if ((s >= DUR && loaded) || skipped) return resolve();
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
    t.removeEventListener('pointerdown', onSkip);
    window.removeEventListener('keydown', onSkip);
    this.nav.remove(t);
    await ready.catch(() => undefined);
    t.classList.add('leaving');
    await wait(180);
    t.classList.remove('leaving');
    this.show(t, false);
    this.narrator.setCinematic(false);
    this.popAux(scene);
    scene.dispose();
  }

  // ---------------------------------------------------------------------------
  // Ending
  // ---------------------------------------------------------------------------

  private endingPedestal?: PedestalScene;

  showEnding(info: EndingInfo): void {
    const g = this.game;
    const e = this.endingEl;
    const n = info.node;
    const ending = n.ending ?? { title: n.name, text: '' };
    const found = new Set([...g.profile.data.discovered, ...info.history]);
    const endingsFound = info.endingsFound.filter((x) => ENDING_STAGES.includes(x)).length;

    this.endingPedestal ??= new PedestalScene({ assets: g.assets, quality: g.quality });
    this.endingPedestal.screenX = window.innerWidth < 900 ? 0.8 : 0.74;
    this.endingPedestal.screenY = 0.48;
    this.endingPedestal.spin = 0.35;
    void this.endingPedestal.show(n.id);
    this.pushAux(this.endingPedestal);
    g.renderSystem.configure({ bloom: { intensity: 1.1, threshold: 0.4 }, vignette: { offset: 0.25, darkness: 0.65 }, chromatic: 0 });

    e.innerHTML = `<div class="ending-body">
      <div class="kick">${ICONS.star}${n.kind === 'final' ? 'TRUE ENDING · ' : ''}ENDING ${endingsFound} / ${ENDING_STAGES.length} DISCOVERED</div>
      <h1 style="color:${n.color}">${letters(ending.title)}</h1>
      <div class="text">${escapeHtml(ending.text)}</div>
      <div class="stats">
        <div class="stat"><span>Time</span> <b>${fmtTime(info.playTime)}</b></div>
        <div class="stat"><span>Forms</span> <b>${info.history.length}</b></div>
        <div class="stat"><span>Devolutions</span> <b>${info.devolutions}</b></div>
        <div class="stat gold"><span>Traits</span> <b>${info.traits.length}</b></div>
      </div>
      <div class="lineage-tree">${buildTreeSvg({ found, endings: new Set(info.endingsFound), highlight: info.history, current: n.id, labels: false })}</div>
      <div class="lineage">${info.history.map((s) => `<span style="color:${TREE[s].color}">${escapeHtml(TREE[s].name)}</span>`).join('<i> → </i>')}</div>
      ${info.traits.length ? `<div class="traits">${info.traits.map((t) => `<span>${escapeHtml(t.name)}</span>`).join('')}</div>` : ''}
      <div class="btn-row"></div></div>`;
    const row = e.querySelector('.btn-row') as HTMLElement;
    const again = navButton(`${ICONS.dna}Devolve &amp; Start Over`, 'primary small', () => void this.startRun('cell'));
    again.dataset.navDefault = '';
    row.append(
      again,
      navButton(`${ICONS.tree}Evolution Tree`, 'small', () => this.openCodex(n.id)),
      navButton('Title Screen', 'small ghost', () => {
        this.hideEnding();
        g.showTitle();
      }),
    );
    this.show(e, true);
    this.narrator.root.classList.add('side');
    this.nav.clear();
    this.nav.push({ root: e });
    const line = ENDING_LINES[n.id];
    if (line) setTimeout(() => void this.narrator.say(line, { mood: n.kind === 'final' ? 'proud' : 'snark', priority: 5 }), 1800);
    if (n.kind === 'final') setTimeout(() => this.rollCredits(), g.debug.autopilot ? 0 : 7000);
  }

  private hideEnding(): void {
    if (!this.endingEl.classList.contains('visible')) return;
    this.show(this.endingEl, false);
    this.narrator.root.classList.remove('side');
    this.nav.remove(this.endingEl);
    this.endingEl.querySelector('.credits')?.remove();
    if (this.endingPedestal) this.popAux(this.endingPedestal);
  }

  private rollCredits(): void {
    const e = this.endingEl;
    if (!e.classList.contains('visible') || e.querySelector('.credits')) return;
    const section = (h: string, rows: Array<[string, string?]>) =>
      `<h2>${h}</h2>${rows.map(([a, b]) => `<p>${escapeHtml(a)}${b ? `<small>${escapeHtml(b)}</small>` : ''}</p>`).join('')}`;
    const cr = el(
      'div',
      'credits show',
      `<div class="roll" style="--dur:${this.game.profile.settings.reducedMotion ? 60 : 38}s">
        <h1>EVOLUTION</h1><p><small>From a single cell to you, in one sitting.</small></p>
        ${section('Starring', [['You', 'every single form'], ['The Scientist', 'as himself, unfortunately']])}
        ${section('Made by a very stressed team of AIs', [
          ['Orchestrator', 'engine, flow, deployment'],
          ['Microbe', 'cell · multicellular colony'],
          ['Drifters', 'algae · jellyfish'],
          ['Ocean', 'fish · shark'],
          ['Land', 'amphibian · reptile'],
          ['Predators', 'dinosaur · wolf'],
          ['Bird', 'bird'],
          ['Mammal', 'early mammal'],
          ['Whale', 'whale'],
          ['Primates', 'primate · chimpanzee'],
          ['Human', 'human'],
          ['Art', 'Blender pipeline & shared FX'],
          ['Audio', 'procedural music, sfx & voice'],
          ['UX', 'menus, HUD, cinematics'],
          ['QA Lead', 'died thousands of times so you did not have to'],
        ])}
        ${section('Made with', [['three.js'], ['Blender'], ['Web Audio'], ['TypeScript & Vite']])}
        ${section('Special thanks', [['3.8 billion years of ancestors'], ['Every organism that did not make it']])}
        <div class="fin">See you at the bottom.</div>
      </div><button class="btn small ghost skip" data-nav>Skip</button>`,
    );
    const close = () => {
      cr.remove();
      this.nav.remove(cr);
    };
    cr.addEventListener('click', close);
    const roll = cr.querySelector('.roll') as HTMLElement;
    roll.addEventListener('animationend', close);
    e.append(cr);
    this.nav.push({ root: cr, onBack: close, onStart: close });
  }

  // ---------------------------------------------------------------------------

  setTouchControls(visible: boolean): void {
    this.touch.root.classList.toggle('visible', visible);
    this.hud.root.classList.toggle('touch-mode', visible);
  }

  showFps(visible: boolean): void {
    this.fps.classList.toggle('visible', visible);
  }

  private fpsAt = 0;
  setFps(fps: number): void {
    const now = performance.now();
    if (now - this.fpsAt < 500 || !this.fps.classList.contains('visible')) return;
    this.fpsAt = now;
    this.fps.textContent = `${Math.round(fps)} fps`;
  }
}
